import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Archive, ChevronUp, ChevronDown, Download, Lock } from "lucide-react";
import axios from "axios";
import { API, csvUrl, apiVaultExportUrl } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/utils";

export default function InventoryVault({ sessionId, plan, refreshKey, onUpgrade }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const load = () => {
    axios.get(`${API}/vault/${sessionId}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total_value || 0);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [sessionId, refreshKey]);

  const isPro = plan === "pro";
  const hasItems = items.length > 0;

  // Hide the persistent bar entirely when the vault is empty — no signal, only
  // noise, and it was covering the footer on mobile. It'll reappear the moment
  // a real scan lands.
  if (!hasItems) return null;

  const download = (scanId) => {
    if (!isPro) {
      toast.error("CSV export is a Pro feature");
      onUpgrade();
      return;
    }
    const a = document.createElement("a");
    a.href = csvUrl(scanId);
    a.download = `resell-ai-${scanId.slice(0, 8)}.csv`;
    a.click();
  };

  const downloadAll = () => {
    if (!isPro) {
      toast.error("Bulk CSV export is a Pro feature");
      onUpgrade();
      return;
    }
    const a = document.createElement("a");
    a.href = apiVaultExportUrl(sessionId);
    a.download = `rsell-vault.csv`;
    a.click();
    toast.success("Exporting your full vault…");
  };

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ delay: 0.4, type: "spring", damping: 24, stiffness: 200 }}
      className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none"
    >
      <div className="max-w-5xl mx-auto px-2 sm:px-4 pointer-events-auto">
        <motion.div
          layout
          className="glass-strong rounded-t-2xl border-b-0 overflow-hidden shadow-[0_-8px_40px_-12px_rgba(15,23,42,0.18)]"
        >
          <button
            data-testid="vault-toggle"
            onClick={() => setOpen((o) => !o)}
            className="w-full px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 hover:bg-white/[0.03] transition-colors"
          >
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#0F172A] flex items-center justify-center flex-shrink-0">
              <Archive size={15} className="text-white" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/50 font-mono-num">My Vault</div>
              <div className="text-xs sm:text-sm text-white font-semibold truncate">
                <span data-testid="vault-total" className="font-mono-num text-[#10B981]">{money(total)}</span>
                <span className="text-white/50 font-normal"> · </span>
                <span data-testid="vault-count" className="font-mono-num">{items.length}</span>
                <span className="text-white/50 font-normal"> {items.length === 1 ? "item" : "items"}</span>
              </div>
            </div>
            <div className="text-white/40 flex-shrink-0 flex items-center gap-2">
              {open && isPro && (
                <button
                  data-testid="vault-export-all-btn"
                  onClick={(e) => { e.stopPropagation(); downloadAll(); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 text-white text-[10px] font-semibold transition-colors"
                  title="Export the whole vault as CSV"
                >
                  <Download size={11} /> Export all
                </button>
              )}
              {open ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                key="body"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28 }}
                className="border-t border-white/[0.06]"
              >
                <div className="p-4 max-h-[46vh] overflow-auto hide-scrollbar">
                  {items.length === 0 ? (
                    <div className="py-8 text-center text-white/50 text-sm">
                      Run your first scan — your vault will populate automatically.
                    </div>
                  ) : (
                    <ul data-testid="vault-list" className="space-y-2">
                      {items.map((it) => (
                        <li
                          key={it.scan_id}
                          className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.15] transition-colors"
                        >
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                            {it.thumb_b64 ? (
                              <img alt="" src={`data:image/jpeg;base64,${it.thumb_b64}`} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">—</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white font-medium truncate">{it.item_title}</div>
                            <div className="text-[11px] text-white/40 font-mono-num uppercase tracking-widest">{it.category}</div>
                          </div>
                          <div className="font-mono-num text-sm text-[#10B981] font-bold flex-shrink-0">{money(it.recommended)}</div>
                          <button
                            data-testid={`vault-export-${it.scan_id}`}
                            onClick={() => download(it.scan_id)}
                            className={`p-2 rounded-lg transition-colors ${
                              isPro
                                ? "hover:bg-white/10 text-white"
                                : "text-white/40 hover:text-white/60"
                            }`}
                            title={isPro ? "Export CSV" : "Pro only — click to upgrade"}
                          >
                            {isPro ? <Download size={14} /> : <Lock size={14} />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {!isPro && items.length > 0 && (
                  <div className="px-4 pb-4">
                    <button
                      data-testid="vault-upgrade-btn"
                      onClick={onUpgrade}
                      className="w-full py-3 rounded-xl bg-[#0F172A] text-white font-semibold text-sm hover:bg-[#1E293B] transition-colors"
                    >
                      Unlock CSV inventory export
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}
